/**
 * Accounts API Module
 *
 * This module provides functions for interacting with the account-related endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import { Account } from "../types/Account";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Retrieves all accounts from the API.
 *
 * @returns A promise that resolves to an array of Account objects.
 * If the request fails, returns an empty array.
 */
export const getAccounts = async (): Promise<Account[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/accounts`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve accounts", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting accounts:", error);
  }

  return [];
};

/**
 * Retrieves a specific account by its ID.
 *
 * @param id - The unique identifier of the account to retrieve.
 * @returns A promise that resolves to an Account object.
 * @throws Will throw an error if the API request fails.
 */
export const getAccountById = async (id: number): Promise<Account> => {
  const response = await axios.get(
    `${getServiceEndpoint()}/accounts/${id}`,
    buildClientHeader()
  );
  return response.data;
};

/**
 * Retrieves all accounts associated with a specific fund.
 *
 * @param fundId - The unique identifier of the fund
 * @returns A promise that resolves to an array of Account objects.
 * If the request fails, returns an empty array.
 */
export const getAccountsByFundId = async (
  fundId: number
): Promise<Account[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/accounts?fundId=${fundId}`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log(
        `Failed to retrieve accounts for fund ${fundId}`,
        response.statusText
      );
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error(`Error getting accounts for fund ${fundId}:`, error);
  }

  return [];
};
